import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * Shared polite live region for swap status announcements. Lives on <body> so it
 * survives the cart section morph (which would otherwise discard an in-drawer
 * region mid-announcement).
 * @param {string} [message]
 */
let politeRegion;
function announce(message) {
  if (!message) return;
  if (!politeRegion) {
    politeRegion = document.createElement('div');
    politeRegion.className = 'visually-hidden';
    politeRegion.setAttribute('role', 'status');
    politeRegion.setAttribute('aria-live', 'polite');
    document.body.appendChild(politeRegion);
  }
  politeRegion.textContent = '';
  requestAnimationFrame(() => {
    politeRegion.textContent = message;
  });
}

/**
 * `<plw-cart-line>` — in-cart variant swap (CQ-1959).
 *
 * Shopify has no "change a line's variant" operation, so swapping Size/Colour on
 * a cart line = re-add the resolved variant + remove the old line. We ADD FIRST so
 * a mid-flight failure never leaves the cart short an item (worst case a visible,
 * recoverable duplicate — never silent data loss). The line's full variant matrix
 * is embedded as JSON so the target variant + its availability resolve without a
 * round-trip.
 *
 * The owning <li> carries a stable id, so the cart section morph tears this element
 * down and recreates it with fresh state after a swap (no stale matrix / stuck busy
 * flag). Progressive enhancement: with JS off the selects are inert and the server-
 * rendered line still shows the chosen variant.
 */
class PlwCartLine extends HTMLElement {
  /** @type {Array<{id:number, available:boolean, options:string[]}>} */
  #variants = [];
  /** @type {HTMLSelectElement[]} */
  #selects = [];
  /** @type {Map<HTMLSelectElement,string>} */
  #previous = new Map();
  #busy = false;
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    const json = this.querySelector('script[data-plw-variants]');
    try {
      this.#variants = json ? JSON.parse(json.textContent || '[]') : [];
    } catch {
      this.#variants = [];
    }

    this.#selects = /** @type {HTMLSelectElement[]} */ ([...this.querySelectorAll('select[data-plw-option]')]);
    for (const select of this.#selects) {
      this.#previous.set(select, select.value);
      select.addEventListener('change', this.#onChange);
    }
  }

  disconnectedCallback() {
    for (const select of this.#selects) {
      select.removeEventListener('change', this.#onChange);
    }
    this.#controller?.abort();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    if (this.#busy) return;

    const changed = /** @type {HTMLSelectElement} */ (event.target);
    const selected = this.#selects.map((select) => select.value);
    const target = this.#variants.find(
      (variant) => variant.options.length === selected.length && variant.options.every((value, index) => value === selected[index])
    );

    // Already the current variant — nothing to do.
    if (target && String(target.id) === this.dataset.variantId) return;

    // No such combination, or it's sold out — revert and tell the user why.
    if (!target || !target.available) {
      changed.value = this.#previous.get(changed) ?? changed.value;
      this.#flash();
      this.#showError(this.dataset.msgUnavailable);
      return;
    }

    this.#swap(target.id, changed);
  };

  /**
   * Re-add the resolved variant, then remove the old line, then morph every cart
   * section. Add-first keeps the swap recoverable on failure.
   * @param {number} newVariantId
   * @param {HTMLSelectElement} changedSelect
   */
  async #swap(newVariantId, changedSelect) {
    this.#busy = true;
    this.#clearError();
    this.setAttribute('aria-busy', 'true');
    this.classList.add('plw-cart__variants--busy');

    const lineKey = this.dataset.lineKey;
    const quantity = parseInt(this.dataset.quantity || '1', 10) || 1;
    const isDrawer = Boolean(this.closest('cart-drawer-component'));
    const changedIndex = changedSelect.dataset.plwOption;
    const sections = this.#sectionIds();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    /** @param {object} payload */
    const body = (payload) =>
      JSON.stringify({ ...payload, sections: sections.join(','), sections_url: window.location.pathname });

    try {
      // 1. Add the new variant first — if this fails, the original line is untouched.
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body: body({ id: newVariantId, quantity }) }),
        signal,
      });
      const addData = await addResponse.json();

      if (!addResponse.ok || addData.status || !addData.sections) {
        this.#fail(changedSelect);
        return;
      }

      // 2. Remove the old line. Its response reflects BOTH operations → morph from it.
      const removeResponse = await fetch(Theme.routes.cart_change_url, {
        ...fetchConfig('json', { body: body({ id: lineKey, quantity: 0 }) }),
        signal,
      });
      const removeData = await removeResponse.json();
      const removed = removeResponse.ok && removeData.sections;
      const payload = removed ? removeData : addData;

      for (const sectionId of sections) {
        const html = payload.sections?.[sectionId];
        if (html) await morphSection(sectionId, html, { mode: isDrawer ? 'hydration' : 'full' });
      }

      // If the remove failed, the cart now holds BOTH variants (a visible, recoverable
      // duplicate — never silent loss). Reconcile from the server so the DOM is honest.
      if (!removed) await this.#reconcile(sections, isDrawer);

      announce(this.dataset.msgUpdated);
      this.#restoreFocus(newVariantId, changedIndex);
      // The morph normally destroys this element; clear state defensively in case it didn't.
      this.#reset();
    } catch (error) {
      if (signal.aborted) return;
      console.error('[plw-cart-line] variant swap failed', error);
      this.#fail(changedSelect);
      // Cart state is uncertain after an exception — reconcile the DOM with the server.
      await this.#reconcile(sections, isDrawer);
    }
  }

  /**
   * Failure cleanup: revert the select, re-enable interaction, surface the error.
   * @param {HTMLSelectElement} changedSelect
   */
  #fail(changedSelect) {
    changedSelect.value = this.#previous.get(changedSelect) ?? changedSelect.value;
    this.#reset();
    this.#showError(this.dataset.msgError);
  }

  #reset() {
    this.#busy = false;
    this.removeAttribute('aria-busy');
    this.classList.remove('plw-cart__variants--busy');
  }

  /**
   * Move focus back to the equivalent control in the (newly rendered) line so a
   * keyboard/AT user isn't dropped to <body> by the morph.
   * @param {number} newVariantId
   * @param {string} [changedIndex]
   */
  #restoreFocus(newVariantId, changedIndex) {
    requestAnimationFrame(() => {
      const line = document.querySelector(`plw-cart-line[data-variant-id="${newVariantId}"]`);
      const select =
        changedIndex != null
          ? line?.querySelector(`select[data-plw-option="${changedIndex}"]`)
          : line?.querySelector('select');
      // The select is wrapped by <plw-select> (a hidden value carrier) — focus its trigger.
      const target = select?.closest('plw-select')?.querySelector('.plw-select__trigger') ?? select;
      if (target instanceof HTMLElement) target.focus();
    });
  }

  /** @returns {string[]} all live cart section ids to re-render. */
  #sectionIds() {
    const ids = new Set();
    for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
      const id = /** @type {HTMLElement} */ (component).dataset.sectionId;
      if (id) ids.add(id);
    }
    return [...ids];
  }

  /**
   * Re-fetch + morph the cart sections from the server (recovery path).
   * @param {string[]} sections
   * @param {boolean} isDrawer
   */
  async #reconcile(sections, isDrawer) {
    for (const sectionId of sections) {
      try {
        const html = await (await fetch(`${window.location.pathname}?section_id=${sectionId}`)).text();
        await morphSection(sectionId, html, { mode: isDrawer ? 'hydration' : 'full' });
      } catch (error) {
        console.error('[plw-cart-line] reconcile failed', error);
      }
    }
  }

  /** @param {string} [message] */
  #showError(message) {
    const row = this.closest('li');
    const container = row?.querySelector('.plw-cart__error');
    const text = row?.querySelector('.plw-cart__error-text');
    if (!container || !text) {
      announce(message);
      return;
    }
    // Un-hide BEFORE writing the text so the role="alert" region is rendered when the
    // text mutates (otherwise several screen readers won't announce it).
    container.classList.remove('hidden');
    text.textContent = message || '';
  }

  #clearError() {
    const row = this.closest('li');
    row?.querySelector('.plw-cart__error')?.classList.add('hidden');
    const text = row?.querySelector('.plw-cart__error-text');
    if (text) text.textContent = '';
  }

  #flash() {
    this.classList.add('plw-cart__variants--unavailable');
    setTimeout(() => this.classList.remove('plw-cart__variants--unavailable'), 600);
  }
}

if (!customElements.get('plw-cart-line')) {
  customElements.define('plw-cart-line', PlwCartLine);
}

/**
 * `<plw-cart-quantity>` — the inline "Quantity: N ⌄" line-item dropdown (CQ-1959).
 *
 * A transparent native <select> over the visible text. On change it dispatches a
 * bubbling QuantitySelectorUpdateEvent, which Horizon's <cart-items-component>
 * already listens for — so the actual cart change, section morph and error
 * handling reuse Horizon's machinery (no duplicate fetch logic here). A hidden
 * <input> mirrors the value and carries `ref="quantitySelectors[]"`, satisfying
 * Horizon's error-revert path (which reads `quantitySelector.querySelector('input')`).
 * Progressive enhancement: with JS off the select is inert and the server-rendered
 * quantity still shows.
 */
class PlwCartQuantity extends HTMLElement {
  /** @type {HTMLSelectElement | null} */
  #select = null;
  /** @type {HTMLInputElement | null} */
  #input = null;
  /** @type {HTMLElement | null} */
  #display = null;

  connectedCallback() {
    this.#select = this.querySelector('select');
    this.#input = this.querySelector('input');
    this.#display = this.querySelector('.plw-cart__select-value');
    this.#select?.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.#select?.removeEventListener('change', this.#onChange);
  }

  #onChange = () => {
    const quantity = parseInt(this.#select?.value ?? '', 10);
    const line = parseInt(this.dataset.line ?? '', 10);
    if (!Number.isFinite(quantity) || !Number.isFinite(line)) return;

    // Keep the visible text + mirror input in sync for the brief window before
    // the cart section morphs this line back in with the server-confirmed value.
    if (this.#display) this.#display.textContent = String(quantity);
    if (this.#input) this.#input.value = String(quantity);

    this.dispatchEvent(new QuantitySelectorUpdateEvent(quantity, line));
  };
}

if (!customElements.get('plw-cart-quantity')) {
  customElements.define('plw-cart-quantity', PlwCartQuantity);
}
